import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import withWidth from '@material-ui/core/withWidth';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import IdleTimer from 'react-idle-timer'
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
// import { useBeforeunload } from 'react-beforeunload';
import Typography from '@material-ui/core/Typography';

import initiateConnection from './actions/connection_actions';
import getProfileFetch from './actions/profile_actions';
import userActions from './actions/user_actions';
import memberActions from './actions/member_actions';
import unreadActions from './actions/unread_message_actions';
import windowActions from './actions/window_actions';
import preferenceActions from './actions/preference_actions';
import LoginPage from './components/LoginPage';
import MainSideBar from './components/MainSideBar';
import { logoutUser } from './actions/logout_actions';
import { closeNamedWindow } from './utils/popupWindows';
import { canNotify, noWindowFocused, showMessageNotification, showAnnouncementNotification } from './utils/desktopNotifications';

import './index.css';
import { flashApp } from './utils/flashNotification';
import { isOn } from './utils/notificationPreferences';

// How stale LastSeen is allowed to get before the server's presence sweeps
// call someone Disconnected is Presence.StaleAfterMs (5 minutes) - keep
// PING_INTERVAL_MS well inside it.
//
// The interval is checked against the wall clock rather than trusted to fire
// on time. setInterval is a main-thread timer: at 100% CPU, or in a tab Chrome
// has throttled for being in the background, ticks arrive late and coalesce
// instead of queueing, so a 30s timer was silently going minutes between
// pings. Polling more often and comparing Date.now() means the first tick
// after the machine frees up pings immediately, rather than starting another
// full interval from wherever the timer happened to land.
const PING_INTERVAL_MS = 30000;
const PING_CHECK_INTERVAL_MS = 5000;

// Every detached window TeamX opens. Closed together on logout and on the
// disconnect countdowns.
const CHILD_WINDOW_NAMES = ['ChatBoxWindow', 'Bulletin', 'MakeAnnouncement', 'Announcement', 'UserManager', 'GroupManager'];

export class App extends Component {
    constructor(props) {
        super(props);
        this.idleTimer1 = null;
        this.timeLeftInterval = null;
        this.networkTimeLeftInterval = null;
        this.pingTimer = null;
        this.lastPingAt = 0;
        this.state = {
            openIsActiveDialog: false,
            openUpdateStatusDialog: true,
            isDisconnected: false,
        };
        this.connection = new HubConnectionBuilder()
            .withUrl("/notifications", { accessTokenFactory: () => localStorage.token })
            // Default is Information, which logs every negotiation - including
            // the socket URL with the JWT in its query string - to the console.
            .configureLogging(LogLevel.Warning)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    return Math.random() * 10000;
                }
            })
            .build();
    }

    
    // Was three copies of an if that tested all six windows for null and then
    // re-opened each one to close it - twelve window.open calls, and all-or-
    // nothing, so one blocked window left the other five open. Note window.open
    // with an empty URL CREATES a window that does not exist, so this has always
    // opened blank windows just to close them again; that is preserved here
    // rather than quietly changed on a release night.
    closeChildWindows = () => {
        CHILD_WINDOW_NAMES.forEach(name => closeNamedWindow(name));
    };

    // Raises a desktop notification for an incoming message, and reports
    // whether it did - the caller uses that to decide whether to chime.
    //
    // Quiet whenever the user can already see the message land: either TeamX
    // window having focus is enough. Also quiet when the sender is not someone
    // we can name, since "New message from undefined" is worse than nothing.
    notifyForMessage = (senderId, text) => {
        // Off means off. Returning false lets the caller fall back to the
        // chime, which is gated on its own preference.
        if (!isOn(this.props.preferences, 'notifications', 'desktop')) {
            return false;
        }

        if (!canNotify() || !noWindowFocused(this.props.chatWindow)) {
            return false;
        }

        const sender = this.props.memberIdMap ? this.props.memberIdMap[senderId] : null;
        const senderName = sender ? (sender.firstName || sender.username) : null;
        if (!senderName) {
            return false;
        }

        // Nothing is passed for a click: clicking a toast must never bring the
        // conversation forward. The body is passed only when the user has
        // asked for previews - off by default, because a toast fires exactly
        // when someone other than the reader may be looking at the screen.
        const note = showMessageNotification({
            senderName: senderName,
            senderId: senderId,
            body: isOn(this.props.preferences, 'notifications', 'preview') ? text : undefined,
        });

        return note !== null;
    };

    // Returns whether a toast fired, so the caller can chime instead - the
    // two stay mutually exclusive, exactly as they are for a message.
    //
    // Takes no title: the toast says "New announcement" and nothing more,
    // for the same reason a message toast names only the sender. The body
    // follows the same preview preference a message does - one switch, so a
    // user who has decided toasts may carry content has decided it once.
    notifyForAnnouncement = (text) => {
        if (!isOn(this.props.preferences, 'notifications', 'announcements')) {
            return false;
        }
        if (!isOn(this.props.preferences, 'notifications', 'desktop')) {
            return false;
        }
        if (!noWindowFocused(this.props.chatWindow)) {
            return false;
        }
        return showAnnouncementNotification({
            body: isOn(this.props.preferences, 'notifications', 'preview') ? text : undefined,
        }) !== null;
    };

    // Stamps LastSeen on the server. Guarded on the connection actually being
    // up: invoke() rejects while the hub is reconnecting, and a rejected ping
    // is a lost ping, which is what the sweeps then read as "gone".
    sendActivePing = () => {
        const userId = this.props.profile && this.props.profile.id;
        if (!userId || !this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve();
        }
        this.lastPingAt = Date.now();
        return this.connection.invoke('SendUserActivePing', userId).catch(err => {
            // The server never saw it, so do not let it count. Zeroing means
            // the next check retries, at most one attempt per check interval.
            this.lastPingAt = 0;
            return console.error("Pinging Error " + err.toString());
        });
    };

    // Keeps retrying rather than giving up after one attempt. A client that
    // stops pinging looks Disconnected to everyone else no matter how much the
    // person is actually using it.
    restartConnection = () => {
        setTimeout(() => {
            this.connection.start()
                .then(() => this.sendActivePing())
                .catch(err => {
                    console.error("Hub restart failed: " + err.toString());
                    this.restartConnection();
                });
        }, 5000);
    };

    maybeSendActivePing = () => {
        if (Date.now() - this.lastPingAt < PING_INTERVAL_MS) {
            return;
        }
        this.sendActivePing();
    };

    // Coming back to the tab is the moment throttling ends and the moment the
    // timestamp is most likely to be stale, so ping rather than wait for the
    // next check.
    handleVisibilityChange = () => {
        if (!document.hidden) {
            this.sendActivePing();
        }
    };

    componentDidMount = () => {
        // The taskbar flash lives in the native handler and survives a
        // reload; state.shouldTurnFlashOn does not. A page that loaded while
        // the handler was flashing believed nothing was, and reading the
        // message could not stop it. The page is on screen when it loads,
        // so stop here. Preferences are not fetched yet, and flashstop never
        // consults them anyway.
        flashApp(this.props.preferences, 'flashstop');
        this.handleConnectionChange();
        this.props.getProfileFetch();
        this.props.fetchPreferences();
        this.props.getUnreadMessagesFromDb();
        // The hub is [Authorize], so negotiating without a token 401s.
        // LoginPage reloads the page after a successful login, at which
        // point componentDidMount runs again and this connects for real.
        if (localStorage.token) {
            this.connection
                .start()
                .then(() => {
                    this.props.initiateConnection(this.connection);

                    // withAutomaticReconnect (see the constructor) handles the
                    // ordinary drop and retries forever, so onclose only fires
                    // if the connection is stopped outright. Retry with a catch
                    // that re-arms: the old version was a single uncaught
                    // start(), and because onclose does not fire again on an
                    // already-closed connection, one failed attempt stranded
                    // the client with no pings until a page reload.
                    this.connection.onclose(error => {
                        this.restartConnection();
                    });

                    // A reconnect leaves LastSeen unstamped for the whole
                    // outage. Close that gap now instead of waiting out another
                    // interval on top of it.
                    this.connection.onreconnected(connectionId => {
                        this.sendActivePing();
                    });

                    this.sendActivePing();
                    this.pingTimer = setInterval(this.maybeSendActivePing, PING_CHECK_INTERVAL_MS);
                }).then(() => {
                    this.connection.on('UpdateUser', (user => {
                        this.props.fetchMember();
                        this.props.getProfileFetch();
                    }));
                    this.connection.on('SetUserStatus', (message => {
                        if (message.indexOf('disconnected') >= 0) {
                            var updatedUser = { ...this.props.profile };
                            this.props.updateProfile(updatedUser);
                        }
                    }));
                    // UserHub already subscribes to NewAnnouncement to render the list.
                    // This second subscription owns the notification side, which needs the
                    // chatWindow handle App holds; SignalR fans one event out to both.
                    // The payload is the announcement Post, so its text is
                    // here to be previewed. This handler used to take no
                    // argument at all and throw it away.
                    this.connection.on('NewAnnouncement', ((notification) => {
                        if (!isOn(this.props.preferences, 'notifications', 'announcements')) {
                            return;
                        }
                        if (flashApp(this.props.preferences, 'flashstart')) {
                            this.props.setIconFlash(true);
                        }
                        var announced = notification && notification.payload
                            ? notification.payload.message
                            : undefined;
                        if (!this.notifyForAnnouncement(announced)) {
                            if (isOn(this.props.preferences, 'notifications', 'sound')) {
                                this.audio = new Audio('./light.mp3');
                                this.audio.play();
                            }
                        }
                    }));

                    this.connection.on('NewMessage', (message => {/////////////////////////////////////////////////////////////////////////////////

                        // A desktop notification and the in-app chime would
                        // otherwise both fire and you would hear two sounds, so
                        // whichever one happens, happens alone. The notification
                        // only goes out when the user is looking at neither
                        // TeamX window - if they can already see the message
                        // arrive, the chime is the right cue.
                        if (message.payload.userId !== this.props.profile.id) {
                            if (!this.notifyForMessage(message.payload.userId, message.payload.message)) {
                                if (isOn(this.props.preferences, 'notifications', 'sound')) {
                                    this.audio = new Audio('./light.mp3');
                                    //this.audio.load();
                                    //this.playAudio();
                                    this.audio.play();
                                }
                            }
                        }


                        if (message.payload.userId !== this.props.profile.id) {
                            this.props.updateUnread(message);
                            if (this.props.profile.hasOwnProperty('id')) { // current signed in user
                                var l = [];
                                for (var property in this.props.unreadMessages) {

                                    if (property.includes("Object")) {
                                        continue;
                                    }
                                    if (this.props.unreadMessages[property]) {
                                        l.push(property);
                                    }
                                }
                                this.props.setUnreadIds(l);
                            } else {
                            }
                        }
                    }));
                })
                //.then(() => {
                //    //this.isuserActiveTest();
                //    const webPing = setInterval(
                //        () => {
                //            fetch('//google.com', {
                //                mode: 'no-cors',
                //            })
                //                .then(() => {
                //                    console.log("inside webping");
                //                    this.setState({ isDisconnected: false }, () => {
                //                        //this.isuserActiveTest();
                //                        return clearInterval(webPing)
                //                    });
                //                }).catch(() => { console.log("inside webping disconnect"); this.setState({ isDisconnected: true }); })
                //        }, 3000);
                //})
                .catch(err => console.error(err.toString()));
        }
        
        window.addEventListener("beforeunload", this.unload);
        window.addEventListener('online', this.handleConnectionChange);
        window.addEventListener('offline', this.handleConnectionChange);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        var sheet = document.createElement('style');
        document.body.appendChild(sheet);
        sheet.innerHTML = '@media (max-width:767px){body{padding-right:0!important}.MuiOutlinedInput-multiline-327{padding:5px!important}.MuiInputBase-root-269{line-height:1.4em!important;padding:5px!important}.MuiDialog-paper-5{margin:5px!important}.MuiTypography-title-227{font-weight:600;font-size:20px}.emoji-mart-scroll{height:150px}.emoji-mart-preview{position:relative;height:60px}a#flashNotification{display:block;position:fixed;bottom:-1px;right:-1px;z-index:-1;opacity:0;width:1px;height:1px}}';
    }
    
    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.unload);
        window.removeEventListener('online', this.handleConnectionChange);
        window.removeEventListener('offline', this.handleConnectionChange);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        clearInterval(this.pingTimer);
    }

    handleConnectionChange = () => {
        const condition = navigator.onLine ? 'online' : 'offline';
        if (condition === 'online') {
            const webPing = setInterval(
                () => {
                    fetch('//google.com', {
                        mode: 'no-cors',
                    })
                        .then(() => {
                            this.setState({ isDisconnected: false }, () => {
                                // Network is back, so LastSeen is as stale as
                                // the outage was long.
                                this.sendActivePing();
                                return clearInterval(webPing)
                            });
                        }).catch(() => this.setState({ isDisconnected: true }))
                }, 2000);
            return;
        }
        return this.setState({ isDisconnected: true }, () => { this.networkStartTimer(); });
    };
    
    unload = (ev) => {
        if (localStorage.getItem('justLoggedIn')) {
            localStorage.removeItem("justLoggedIn");
        } else {
            // Leaving must not leave the taskbar flashing with nothing left
            // on this side that knows to stop it.
            flashApp(this.props.preferences, 'flashstop');
            if (this.props.profile.hasOwnProperty('id')) {
                var updatedUser = { ...this.props.profile };
                updatedUser["status"] = "Done";
                updatedUser["inOffice"] = false;
                updatedUser["stamp"] = "validLogout";
                this.props.updateProfile(updatedUser);
                this.props.updateLastSeen();
            } else {
                return ev;
            }
            localStorage.removeItem("token");
            this.props.setChatDisplay(false);
            //
            this.closeChildWindows();
            //
        }
    }
    
    /*
    
    // Chat Window Styling
    jss675 {padding 0px}
    jss673 {padding 18.5px 14px}
    jss281 {resize none; padding 0px}
    jss279 {font inherit; color currentcolor...}
    
    MuiOutlinedInput-inputMultiline-331 {padding 0px}
    MuiOutlinedInput-input-329 {padding 18.5px 14px}
    MuiInputBase-inputMultiline-281 {resize none; padding 0px}
    MuiInputBase-input-279 {font inherit; color currentcolor...}
    
    
    
    // Body
      @media(max-width: 767px) {
      body {
        padding-right: 0!important
      }
        .MuiOutlinedInput-multiline-327 {
        padding: 5px!important
      }
        .MuiInputBase-root-269 {
        line-height: 1.4em!important;
        padding: 5px!important
      }
        .MuiDialog-paper-5 {
        margin: 5px!important
      }
        .MuiTypography-title-227 {
        font-weight: 600;
        font-size: 20px
      }
        .emoji-mart-scroll {
        height: 150px
      }
        .emoji-mart-preview {
        position: relative;
        height: 60px
      }
      a#flashNotification {
        display: block;
        position: fixed;
        bottom: -1px;
        right: -1px;
        z - index: -1;
        opacity: 0;
        width: 1px;
        height: 1px
      }
    }
    
     
    
    
    
    
      @media(max-width: 767px) {
      body {
        padding-right: 0!important
      }
        .jss327 {
        padding: 5px!important
      }
        .jss269 {
        line-height: 1.4em!important;
        padding: 5px!important
      }
        .jss5 {
        margin: 5px!important
      }
        .jss227 {
        font-weight: 600;
        font-size: 20px
      }
        .emoji-mart-scroll {
        height: 150px
      }
        .emoji-mart-preview {
        position: relative;
        height: 60px
      }
      a#flashNotification {
        display: block;
        position: fixed;
        bottom: -1px;
        right: -1px;
        z - index: -1;
        opacity: 0;
        width: 1px;
        height: 1px
      }
    }
    
    */

    onIdle1 = (e) => {
        if (!this.state.openIsActiveDialog && localStorage.token) {
            this.setState({ openIsActiveDialog: true });
            this.startTimer();
        }
    };

    handleIsActiveDialogOpen = () => {
        this.setState({ openIsActiveDialog: true });
    };

    handleIsActiveDialogClose = () => {
        if (this.state.timeLeftInterval != null) {
            clearInterval(this.state.timeLeftInterval);
        }
        this.setState({ openIsActiveDialog: false });
    };
    
    handleUpdateStatusDialogOpen = () => {
        this.setState({ openUpdateStatusDialog: true });
    };

    handleUpdateStatusDialogClose = () => {
        this.setState({ openUpdateStatusDialog: false });
    };

    handleNetworkLossReload = () => {
        flashApp(this.props.preferences, 'flashstop');
        setTimeout(() => window.location.reload(), 100);
    };

    
    startTimer = () => {
        var timeLeft = 30;
        //const now = Date.now();
        //const timeBeforeForcedLogout = 0.5 * 60 * 60 * 1000;
        //var updatedUser = { ...this.props.profile };
        var downloadTimer = setInterval(function () {
            //document.getElementById("countdown").innerHTML = timeLeft;
            timeLeft -= 1;
            if (timeLeft < 0) {
                if (this.state.openIsActiveDialog) {
                    this.setState({ openIsActiveDialog: false });
                    this.handleIsActiveDialogClose();

                    var updatedUser = { ...this.props.profile };
                    updatedUser["Status"] = "Done";
                    updatedUser["inOffice"] = false;
                    this.props.updateProfile(updatedUser);

                    flashApp(this.props.preferences, 'flashstop');
                    localStorage.removeItem("token");
                    this.props.logoutUser();
                    this.props.setChatDisplay(false);
                    setTimeout(() => window.location.reload(), 500);
                }
                clearInterval(downloadTimer);
            }
            
        }.bind(this), 1000);

        this.setState({ 'timeLeftInterval': downloadTimer });
        //
        this.closeChildWindows();
        //
    };
    
    
    networkStartTimer = () => {
        var timeLeft = 10;
        var networkDownloadTimer = setInterval(function () {
            document.getElementById("networkCountdown").innerHTML = timeLeft;
            timeLeft -= 1;
            if (timeLeft < 0) {
                if (this.state.isDisconnected) {
                    flashApp(this.props.preferences, 'flashstop');
                    setTimeout(() => window.location.reload(), 500);
                }
                clearInterval(networkDownloadTimer);
            }
        }.bind(this), 1000);
        this.setState({ 'networkTimeLeftInterval': networkDownloadTimer });
        //
        this.closeChildWindows();
        //
    };
    
    render() {
        let updateStatusDialog;
        if (this.props.profile.status === "Disconnected") {
            updateStatusDialog =
                <Dialog
                    open={this.state.openUpdateStatusDialog}
                    aria-labelledby="form-dialog-title"
                    style={{ margin: '5px !important' }}
                    PaperProps={{ style: { minWidth: '190px' } }}
                >
                    <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>You've Disconnected!</DialogTitle>
                    <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
                        Please update your status when you have reconnected.
          </DialogContent>
                    <DialogActions style={{ margin: '0px' }}>
                        <Button onClick={this.handleUpdateStatusDialogClose} color="primary" style={{ margin: '0px', fontSize: '10px' }}>
                            OK
            </Button>
                    </DialogActions>
                </Dialog>;
        } else {
            updateStatusDialog = <div></div>;
        }

        let networkLossDialog =
            <Dialog
                open={true}
                aria-labelledby="form-dialog-title"
                style={{ margin: '5px !important' }}
                PaperProps={{ style: { minWidth: '190px' } }}
            >
                <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>
                    <Typography style={{ fontSize: '14px', fontWeight: 'bold' }}>You've Disconnected!</Typography>
                </DialogTitle>
                <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
                    This page will refresh in <span id="networkCountdown"></span> seconds. Please check your internet connection.
                </DialogContent>
            </Dialog>;
        {/*
      <Dialog
        open={true}
        aria-labelledby="form-dialog-title"
        style={{ margin: '5px !important' }}
        PaperProps={{ style: { minWidth: '190px' } }}
      >
        <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>
          <Typography style={{ fontSize: '14px', fontWeight: 'bold' }}>You've Disconnected!</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
          Please click OK to reload and check your internet connection.
        </DialogContent>
        <DialogActions style={{ margin: '0px', padding: '0px' }}>
          <Button onClick={this.handleNetworkLossReload} color="primary" style={{ marginRight: '5px', marginBottom: '5px', fontSize: '10px' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>;
      */}

        const { width } = this.props;
        return (
            <div>
                {/*this.state.isDisconnected && networkLossDialog && (this.props.setDisconnected(true) || true)*/}
                {this.state.isDisconnected && networkLossDialog}
                <IdleTimer
                    ref={ref => { this.idleTimer1 = ref }}
                    element={document}
                    onIdle={this.onIdle1}
                    debounce={250}
                    timeout={4 * 60 * 60 * 1000} // 4 hours
                    //timeout={10000} // 10 seconds
                />
                <Dialog
                    open={this.state.openIsActiveDialog}
                    aria-labelledby="form-dialog-title"
                    style={{ margin: '5px !important' }}
                    PaperProps={{ style: { minWidth: '190px' } }}
                >
                    <DialogTitle id="form-dialog-title" style={{ paddingTop: '5px', paddingLeft: '5px', paddingBottom: '0px' }}>
                        <Typography style={{ fontSize: '14px', fontWeight: 'bold' }}>Still Active?</Typography>
                    </DialogTitle>
                    <DialogContent style={{ paddingLeft: '5px', paddingRight: '5px', paddingBottom: '5px', paddingTop: '10px', fontSize: '11px' }}>
                        Please respond within <span id="countdown"></span> seconds.
                    </DialogContent>
                    <DialogActions style={{ margin: '0px', paddingTop: '0px' }}>
                        <Button onClick={this.handleIsActiveDialogClose} color="primary" style={{ margin: '0px', fontSize: '10px' }}>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
                {updateStatusDialog}
                <LoginPage />
                <MainSideBar className="test" />
            </div>
        );
    }
}

const mapDispatchToProps = dispatch => ({
    logoutUser: () => dispatch(logoutUser()),
    initiateConnection: (connection) => dispatch(initiateConnection['INITIATE_CONNECTION'](connection)),
    getProfileFetch: () => dispatch(getProfileFetch['LOAD_PROFILE']()),
    fetchMember: () => dispatch(memberActions['FETCH_MEMBERS']()),
    updateUnread: (message) => dispatch(userActions['SET_UNREAD_MESSAGE'](message)),
    setDisconnected: (bool) => dispatch(userActions['SET_DISCONNECTED'](bool)),
    //updateLastSeen: () => dispatch(userActions['UPDATE_LAST_SEEN']()),
    updateLastSeen: (updateStamp) => dispatch(userActions['UPDATE_LAST_SEEN'](updateStamp)),
    updateStamp: () => dispatch(userActions['UPDATE_STAMP']()),
    setChatDisplay: shouldDisplay => dispatch(windowActions['SET_CHAT_DISPLAY'](shouldDisplay)),
    fetchPreferences: () => dispatch(preferenceActions['FETCH_PREFERENCES']()),
    savePreferences: items => dispatch(preferenceActions['SAVE_PREFERENCES'](items)),
    setIconFlash: shouldTurnFlashOn => dispatch(windowActions['SET_ICON_FLASH'](shouldTurnFlashOn)),
    updateProfile: user => dispatch(getProfileFetch['UPDATE_PROFILE'](user)),
    setUnreadIds: unreadIds => dispatch(unreadActions['SET_UNREAD_IDS'](unreadIds)),
    getUnreadMessagesFromDb: () => dispatch(unreadActions['GET_UNREAD_IDS']()),
})

function mapStateToProps(state) {
    return {
        profile: state.profile,
        memberIdMap: state.memberIdMap,
        chatWindow: state.chatWindow,
        shouldAllowWindowAutoClose: state.shouldAllowWindowAutoClose,
        preferences: state.preferences,
        unreadMessages: state.unreadMessages,
        shouldTurnFlashOn: state.shouldTurnFlashOn,
        //shouldDisplayChat: state.shouldDisplayChat,
    };
}

App.propTypes = {
    width: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withWidth()(App));

//export default connect(null, mapDispatchToProps)(App);


/*

Next Phase:
-Prompt user to update status when back by time is reached
-emoji :id: shortcut [ChatBoxCompose.js]
-bring chatbox up using focus
-Remove white bar in opened windows
-opens new chat window when double click after refreshing, even if chat is already opened (don't log out or refresh)
-when logout, auto set status to unavailable & close all opened windows (don't log out)
-close all open windows when logout or close memberList (don't log out)
-create a callback to parent component (sideMenuBar) losing MainMenu closes Bulletin window
-add spinner for save button (userManager and groupManager)
-when user is not back by their specified away time, dialog pops up to change status [UserInfo.js]
-away time formating from time picker is 24hr but need to display 12hr AM/PM [UserInfo.js; handleChange function]
-announcements
-connect to odyssey API for bulletin
-update bulletin with API calls throughout the day + motivational quote API
-permissions to search all conversations for admin


==== Features ====
-bulletin board
  -display sales progress & goals
  -motivational quotes API
-User manager
  -create a new user (name, extension, password, email)
  -set name, extension, password, permissions
  -change existing users
-Group mananger
  -create a group
  -add a member to group
  -remove a member from a group
  -list of all groups and their members
Login
  -user logs in via email/password
  -if enter invalid email/password, error message appears
  -when login successful, user will stay logged in for x amt of days
  -user can logout
  -press enter to login

Browser sidebar (MatterMost)
  -Expand1: list of all members, showing their status, hover for phone numbers
  -Expand2: Group Messages/Channels & Direct messages
    -unread messages (number of unread messages in browser tab & light up taskbar icon)
    -users' status
    -number people in the group
    -List group members' names... OR group name that they can create
    -hover over message to expose x to close the chat

Pop out member list
  -look & function same as SM list
  -displays name, extension, in/out of office, away message clock icon, who is oncall via MM logo icon
  -hover over name for name and phone numbers
  -pop out button should be bottom of left side bar
  -show & change user's status on pop-out list

User status + Away message
  -user selects from 4 different statuses next to user's name
  -user can enter in away message + away time (optional)
  -away message includes a text box and a time picker to select what time they will be back (how to handle when some people say ASAP?)
  -when that time comes, show pop up alert propmpting user to update status (will users still receive messages while alert is not clicked?)
  -everytime they login, should status be available?
  -when change status, should be reflected in list
  -away message -> cancel: doesn't change status to busy
                -> no message: unbold, black font color name
               -> ok: unbold, black font color name, clock symbol, message & time required
  -when submit away mssg & back time, user will be prompted to change their status at the time they inputed
  
Edit profile
  -user can edit their profile (name, address, phone numbers, email, extension) (email address that they login with)
  -user can edit their work schedule (should this be time pickers too?)

On-call schedule
  -only available to those with group/role Admin
  -select on-call schedule for each company (MarketMaps, GBBIS, etc.)
  -persist on-call schedule from previous day
  -emails on-call schedule to specified recipients 
  -specified recipients vary between each company (persist the email recipients for each company)
  -what if no email recipients are selected and press "send email"?

Chat messaging
  -chat instant messaging box by double clicking user's name on pop-out list
  -only one chat box; multiple tabs for multiple chats
  -chat messaging window in browser tab
  -lists direct messages + group direct messages, private channels, public channels
  -emojis

User Manage + Manage Groups
  -UI to add a new employee to TeamX
  -Set up their name, phone extension, password, email, user permissions (Allow the ability to manage users, Allow the ability to change the on call schedule, Allow the ability to view log files)
  -UI for adding a member to a group (ex: Admin, Developers, Sales, etc.)

View progress and goals
  -displays company sales via percentage bars, dollar amt goal, time frame, this year, last year
  -motivational quote
  -business days left (There is 1 day left in the week, There are 16 days left in the month, etc.)
  


App
 |
 | __ LoginPage
 |
 | __ MainSideBar
 |     |
 |     | __ UserHub (logout, main menu, bulletin, user/group manager)
 |     |     |
 |     |     | __ UserInfo (user's name, avatar, status symbol, change status, add away message)
 |     |     |
 |     |     | __ MainMenu (options: edit profile/schedule, select on-call schedule if admin role, logout)
 |     |           |
 |     |           | __ Profile (edit your profile/schedule)
 |     |           |
 |     |           | __ Bulletin
 |     |           |
 |     |           | __ OnCallScheduleList (on hold)
 |     |                 |
 |     |                 | __ OnCallScheduleItem (on hold)
 |     |
 |     | __ MemberList
 |           | 
 |           | __ MemberListItem
 |           |
 |           | __ ChatBoxTabsWindow (fetches messages array and passes it to MessageList which passes it to MessageListItem)
 |                  |
 |                  | __ ChatBoxMessageList
 |                  |     |
 |                  |     | __ ChatBoxMessageListItem
 |                  |
 |                  | __ ChatBoxCompose (adds message obj to messages array)
 |     





 |     | __ DirectMessageList (Tab2)
 |           |
 |           | __DirectMessageListItem
 |     
 | __ WideChatWindow
 |     |
 |     | __ WideChatTopBar
 |     |
 |     | __ WideChatMessageDisplay
 |     |     |
 |     |     | __ WideChatMessageDisplayItem
 |     |
 |     | __ WideChatMessageCompose
 */


// WEBPACK FOOTER //
// ./src/App.js